import type {
	OpaqueData,
	PaymentGatewayConfig,
	PaymentMethodFormData,
} from '../../types/payment.types';
import {
	PaymentGatewayError,
	PaymentMethodTypeEnum,
} from '../../types/payment.types';
import {
	isAcceptedRoutingNumber,
	normalizeRoutingNumber,
	ROUTING_NUMBER_INVALID_MESSAGE,
} from '../../utils/bank-routing-number';
import { ApiService } from '../ApiService';
import type {
	AdyenCardFrameInitMessageModel,
	AdyenCardFrameLabelsModel,
	AdyenCardFramePlaceholdersModel,
	AdyenCardFrameStylesModel,
	AdyenSecuredFieldEventModel,
} from './adyen-card-frame.protocol';
import {
	ADYEN_CARD_FRAME_PROTOCOL,
	isAdyenAllowedOriginProtocol,
	parseAdyenCardFrameToParentMessage,
} from './adyen-card-frame.protocol';
import { collectBrowserInfo } from './collect-browser-info';

// ============================================================================
// Adyen Web SDK Types (v6, loaded via the checkoutshopper CDN embed —
// exposes the window.AdyenWeb global)
// ============================================================================

interface AdyenCardChangeState {
	isValid: boolean;
	data?: {
		paymentMethod?: Record<string, string>;
	};
}

interface AdyenBrandEvent {
	brand?: string;
}

interface AdyenFieldValidEvent {
	fieldType?: string;
	endDigits?: string;
	valid?: boolean;
}

interface AdyenBinValueEvent {
	binValue?: string;
	uuid?: string;
}

interface AdyenCustomCardComponent {
	mount: (target: HTMLElement | string) => AdyenCustomCardComponent;
	unmount: () => void;
}

interface AdyenCheckoutInstance {
	options?: Record<string, unknown>;
}

interface AdyenCustomCardConstructor {
	new (
		checkout: AdyenCheckoutInstance,
		options: {
			brands?: Array<string>;
			styles?: Record<string, Record<string, string>>;
			placeholders?: {
				cardNumber?: string;
				expiryDate?: string;
				securityCodeThreeDigits?: string;
				securityCodeFourDigits?: string;
			};
			onChange?: (state: AdyenCardChangeState) => void;
			onBrand?: (event: AdyenBrandEvent) => void;
			onFieldValid?: (event: AdyenFieldValidEvent) => void;
			onBinValue?: (event: AdyenBinValueEvent) => void;
		},
	): AdyenCustomCardComponent;
}

interface AdyenWebGlobal {
	AdyenCheckout: (config: {
		clientKey: string;
		environment: string;
		countryCode: string;
		locale?: string;
		analytics?: { enabled: boolean };
	}) => Promise<AdyenCheckoutInstance>;
	CustomCard: AdyenCustomCardConstructor;
}

declare global {
	interface Window {
		AdyenWeb?: AdyenWebGlobal;
	}
}

type PaymentContextType = 'CARD' | 'CHECK';

/** How long the card-entry frame gets to report its fields mounted. */
const CARD_FRAME_MOUNT_TIMEOUT_MS = 35000;

interface CardFrameStateModel {
	iframe: HTMLIFrameElement;
	removeListeners: () => void;
}

// ============================================================================
// ADYEN FRONTEND SERVICE
// ============================================================================

/**
 * Client-side Adyen integration (PD-14757).
 *
 * Cards use Adyen's Custom Card secured fields: the SDK injects hosted
 * iframes into our field containers (tagged with data-cse attributes), and
 * exposes encrypted card blobs through onChange state — the raw PAN never
 * touches our code. The encrypted paymentMethod object is serialized into
 * the standard OpaqueData shape and vaulted server-side.
 *
 * ACH uses the plain form inputs: bank account details are not PCI data
 * and are sent to the backend as-is (same approach as the Adyen API-only
 * ACH integration), serialized into the same OpaqueData shape.
 *
 * A page whose address Adyen cannot list as an allowed origin (the iOS
 * storefront app, `capacitor://…`) mounts the card fields inside the
 * storefront's https card-entry page instead (PD-17168,
 * createFramedCardFields). The frame forwards Adyen's events and this
 * service applies them exactly as it applies its own, so everything after
 * mounting is identical.
 */
export class AdyenFrontendService {
	private _config: PaymentGatewayConfig | null = null;
	private _isCardInitialized = false;
	private _isCheckInitialized = false;

	private _checkout: AdyenCheckoutInstance | null = null;
	private _customCard: AdyenCustomCardComponent | null = null;
	private _cardFrame: CardFrameStateModel | null = null;

	// Latest secured-fields state from onChange
	private _cardStateValid = false;
	private _cardPaymentMethodData: Record<string, string> | null = null;
	private _detectedCardBrand: string | null = null;
	private _cardLastFour: string | null = null;
	private _cardBin: string | null = null;
	private _onCardBrandChange: ((brand: string | null) => void) | null = null;
	private _onCardBinChange: ((bin: string | null) => void) | null = null;

	/**
	 * Pinned SDK version. Version-pinned CDN embed per Adyen's docs; update
	 * deliberately (the SDK renders payment fields — treat bumps like a
	 * dependency upgrade, not a config tweak).
	 */
	private static readonly _sdkVersion = '6.6.0';

	// Static flag to track if preloading has been started
	private static _preloadStarted = false;

	private static _getSdkBaseUrl(environment?: string): string {
		const host =
			environment === 'production'
				? 'checkoutshopper-live.adyen.com'
				: 'checkoutshopper-test.adyen.com';

		return `https://${host}/checkoutshopper/sdk/${AdyenFrontendService._sdkVersion}`;
	}

	/**
	 * Preload the Adyen Web SDK script + stylesheet for faster
	 * initialization later.
	 */
	public static preloadScript(environment?: string): void {
		// The SDK runs inside the card-entry frame on such a page, never here.
		if (
			AdyenFrontendService._preloadStarted ||
			AdyenFrontendService.needsCardFrame()
		) {
			return;
		}

		AdyenFrontendService._preloadStarted = true;

		const baseUrl = AdyenFrontendService._getSdkBaseUrl(environment);
		AdyenFrontendService._injectAssets(baseUrl);
	}

	/**
	 * PD-17168 — whether this page's own address can never be an Adyen
	 * allowed origin (the iOS storefront app runs from `capacitor://…`), so
	 * the card fields must be mounted with createFramedCardFields instead.
	 */
	public static needsCardFrame(): boolean {
		return (
			typeof window !== 'undefined' &&
			!isAdyenAllowedOriginProtocol(window.location.protocol)
		);
	}

	/**
	 * PD-17168 — read the card-entry frame's styles off a real, themed form
	 * field and its label, so the framed fields match the fields around them
	 * in both light and dark mode without the frame knowing the theme.
	 * Colors that only exist in a state the reference cannot be in (focus,
	 * placeholder, error) are passed explicitly.
	 */
	public static resolveCardFrameStyles(
		frameContainer: HTMLElement,
		field: HTMLElement,
		label: HTMLElement,
		colors: {
			focusColor: string;
			placeholderColor: string;
			errorColor: string;
			requiredColor: string;
		},
	): AdyenCardFrameStylesModel {
		const fieldStyle = window.getComputedStyle(field);
		const labelStyle = window.getComputedStyle(label);

		// The nearest painted background behind the frame's container.
		let pageBackground = 'transparent';

		for (
			let element: HTMLElement | null = frameContainer;
			element != null;
			element = element.parentElement
		) {
			const background = window.getComputedStyle(element).backgroundColor;

			if (background !== 'transparent' && background !== 'rgba(0, 0, 0, 0)') {
				pageBackground = background;
				break;
			}
		}

		return {
			pageBackground,
			fontFamily: fieldStyle.fontFamily,
			labelColor: labelStyle.color,
			labelFontSize: labelStyle.fontSize,
			labelFontWeight: labelStyle.fontWeight,
			requiredColor: colors.requiredColor,
			fieldBackground: fieldStyle.backgroundColor,
			fieldBorderColor: fieldStyle.borderTopColor,
			fieldBorderWidth: fieldStyle.borderTopWidth,
			fieldBorderRadius: fieldStyle.borderTopLeftRadius,
			fieldBoxShadow: fieldStyle.boxShadow,
			fieldHeight: `${Math.round(field.getBoundingClientRect().height)}px`,
			fieldPaddingX: fieldStyle.paddingLeft,
			fieldFontSize: fieldStyle.fontSize,
			fieldFontWeight: fieldStyle.fontWeight,
			focusColor: colors.focusColor,
			textColor: fieldStyle.color,
			placeholderColor: colors.placeholderColor,
			errorColor: colors.errorColor,
		};
	}

	private static _injectAssets(baseUrl: string): void {
		const scriptUrl = `${baseUrl}/adyen.js`;
		const styleUrl = `${baseUrl}/adyen.css`;

		if (document.querySelector(`script[src="${scriptUrl}"]`) == null) {
			const script = document.createElement('script');
			script.src = scriptUrl;
			script.async = true;
			document.head.appendChild(script);
		}

		// The stylesheet styles the secured-field frames. Their SIZE no longer
		// depends on it — the form's containers stretch the injected iframes
		// themselves (PD-16214) — because a browser that does not apply this
		// cross-origin sheet was left with default-sized frames clipped inside
		// the field boxes.
		if (document.querySelector(`link[href="${styleUrl}"]`) == null) {
			const link = document.createElement('link');
			link.rel = 'stylesheet';
			link.href = styleUrl;
			document.head.appendChild(link);
		}
	}

	/**
	 * Initialize the service by loading configuration and the Adyen Web SDK.
	 *
	 * @param paymentType - 'CARD' for credit cards, 'CHECK' for ACH/eCheck
	 * @param externalConfig Optional pre-fetched gateway config. When provided,
	 *   skips the internal ApiService call — allows non-CRM consumers (e.g. portal)
	 *   to supply config fetched through their own API layer.
	 */
	public async initialize(
		paymentType: PaymentContextType = 'CARD',
		externalConfig?: PaymentGatewayConfig,
	): Promise<void> {
		if (paymentType === 'CARD' && this._isCardInitialized) {
			return;
		}

		if (paymentType === 'CHECK' && this._isCheckInitialized) {
			return;
		}

		try {
			if (this._config == null) {
				this._config = externalConfig ?? (await this._getConfiguration());
			}

			if (this._config.processorType !== 'adyen') {
				throw new PaymentGatewayError(
					'Adyen is not the configured payment processor',
					'WRONG_PROCESSOR',
				);
			}

			// ACH needs no SDK — the plain form fields are serialized directly.
			if (paymentType === 'CHECK') {
				this._isCheckInitialized = true;
				return;
			}

			const clientKey = this._config.publicCredentials.clientKey;

			if (clientKey == null || clientKey === '') {
				throw new PaymentGatewayError(
					'Adyen client key not configured',
					'CONFIG_ERROR',
				);
			}

			// The card-entry frame loads the SDK under its own https address;
			// loading it here as well would only fetch an unused script.
			if (AdyenFrontendService.needsCardFrame()) {
				this._isCardInitialized = true;
				return;
			}

			await this._loadSdk(this._config.environment);

			const adyenWeb = window.AdyenWeb;

			if (adyenWeb == null) {
				throw new PaymentGatewayError(
					'Adyen Web SDK failed to load',
					'INIT_ERROR',
				);
			}

			this._checkout = await adyenWeb.AdyenCheckout({
				clientKey,
				environment:
					this._config.environment === 'production' ? 'live' : 'test',
				countryCode: 'US',
				locale: 'en-US',
				analytics: { enabled: false },
			});

			this._isCardInitialized = true;
		} catch (error) {
			let errorMessage = 'Failed to initialize Adyen service';

			if (error instanceof PaymentGatewayError) {
				errorMessage = error.message;
			} else if (error instanceof Error) {
				errorMessage = error.message;
			}

			throw new PaymentGatewayError(errorMessage, 'INIT_ERROR', error);
		}
	}

	/**
	 * Create and mount the secured card fields.
	 *
	 * Adyen's Custom Card component works differently from Collect.JS: it
	 * mounts once on a shared ancestor and injects an iframe into every
	 * descendant tagged with a data-cse attribute. We tag the three field
	 * containers, then mount on their closest common ancestor.
	 *
	 * @param numberContainerId - container element for the card number field
	 * @param expContainerId - container element for the expiration field
	 * @param cvvContainerId - container element for the CVV field
	 */
	public createSecureCardFields(
		numberContainerId: string,
		expContainerId: string,
		cvvContainerId: string,
		options?: {
			onCardBrandChange?: (brand: string | null) => void;
			/**
			 * PD-16381 — the card's BIN (6-8 digits), or null once the number
			 * no longer holds a complete one. Called only when the value
			 * CHANGES, not on every keystroke, because each call may cost the
			 * caller a funding lookup.
			 */
			onCardBinChange?: (bin: string | null) => void;
			textColor?: string;
			placeholderColor?: string;
			errorColor?: string;
			/** In-field font size; defaults to 14px. */
			fontSize?: string;
			/**
			 * PD-17168 — in-field font; defaults to the system UI stack. Pass
			 * the surrounding inputs' computed font-family to match them.
			 */
			fontFamily?: string;
			/**
			 * PD-17168 — in-field weight; defaults to 400. Adyen's own default
			 * is 200, which reads visibly thinner than the plain inputs.
			 */
			fontWeight?: string;
			/**
			 * PD-17168 — hint text inside the secured fields, so they read like
			 * the plain inputs beside them. Adyen shows none unless given.
			 */
			placeholders?: AdyenCardFramePlaceholdersModel;
			/**
			 * PD-17168 — every secured-field event as it is applied, so the
			 * card-entry frame can forward it to the app that framed it.
			 */
			onSecuredFieldEvent?: (event: AdyenSecuredFieldEventModel) => void;
		},
	): void {
		if (!this._isCardInitialized || this._checkout == null) {
			throw new PaymentGatewayError(
				'Card service not initialized. Call initialize("CARD") first.',
				'NOT_INITIALIZED',
			);
		}

		const adyenWeb = window.AdyenWeb;

		if (adyenWeb == null) {
			throw new PaymentGatewayError(
				'Adyen Web SDK not available',
				'NOT_INITIALIZED',
			);
		}

		const numberContainer = document.getElementById(numberContainerId);
		const expContainer = document.getElementById(expContainerId);
		const cvvContainer = document.getElementById(cvvContainerId);

		if (
			numberContainer == null ||
			expContainer == null ||
			cvvContainer == null
		) {
			throw new PaymentGatewayError(
				'Adyen card field containers not found in the DOM',
				'NOT_INITIALIZED',
			);
		}

		this._onCardBrandChange = options?.onCardBrandChange ?? null;
		this._onCardBinChange = options?.onCardBinChange ?? null;

		// Remount cleanly if the fields were created before (e.g. after a
		// payment-type switch)
		this._unmountCardFields();

		// Clear loading placeholders and tag the containers for the SDK's
		// data-cse scan
		this._prepareContainer(numberContainer, 'encryptedCardNumber');
		this._prepareContainer(expContainer, 'encryptedExpiryDate');
		this._prepareContainer(cvvContainer, 'encryptedSecurityCode');

		const mountRoot = this._findCommonAncestor([
			numberContainer,
			expContainer,
			cvvContainer,
		]);

		const textColor = options?.textColor ?? '#333';
		const placeholderColor = options?.placeholderColor ?? '#9CA3AF';
		const errorColor = options?.errorColor ?? '#DC2626';
		const onSecuredFieldEvent = options?.onSecuredFieldEvent ?? null;
		const handleEvent = (event: AdyenSecuredFieldEventModel): void => {
			this._applySecuredFieldEvent(event);
			onSecuredFieldEvent?.(event);
		};

		this._cardStateValid = false;
		this._cardPaymentMethodData = null;
		this._cardLastFour = null;
		this._cardBin = null;

		const placeholders = options?.placeholders;

		this._customCard = new adyenWeb.CustomCard(this._checkout, {
			brands: ['visa', 'mc', 'amex', 'discover', 'diners', 'jcb'],
			...(placeholders != null
				? {
						placeholders: {
							cardNumber: placeholders.cardNumber,
							expiryDate: placeholders.expiryDate,
							securityCodeThreeDigits: placeholders.securityCode,
							securityCodeFourDigits: placeholders.securityCode,
						},
					}
				: {}),
			// In-iframe text styling — Adyen allows a limited property list here
			// (color, font, caret, background); box styling (border, radius,
			// size) lives on our containers. `background: transparent` is
			// REQUIRED on every state (PD-15014): the iframe's inner input ships
			// Adyen's default WHITE background, which on a dark-themed host
			// renders a white stripe whose light text is unreadable. Transparent
			// lets the themed container paint the field in both modes.
			styles: {
				base: {
					background: 'transparent',
					color: textColor,
					fontSize: options?.fontSize ?? '14px',
					fontFamily:
						options?.fontFamily ??
						'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
					fontWeight: options?.fontWeight ?? '400',
				},
				error: {
					background: 'transparent',
					color: errorColor,
				},
				placeholder: {
					background: 'transparent',
					color: placeholderColor,
					fontWeight: options?.fontWeight ?? '400',
				},
				validated: {
					background: 'transparent',
					color: textColor,
				},
			},
			onChange: (state: AdyenCardChangeState): void => {
				handleEvent({
					kind: 'change',
					isValid: state.isValid,
					paymentMethod: state.data?.paymentMethod ?? null,
				});
			},
			onBrand: (event: AdyenBrandEvent): void => {
				handleEvent({ kind: 'brand', brand: event.brand ?? null });
			},
			onFieldValid: (event: AdyenFieldValidEvent): void => {
				handleEvent({
					kind: 'fieldValid',
					fieldType: event.fieldType ?? null,
					endDigits: event.endDigits ?? null,
				});
			},
			onBinValue: (event: AdyenBinValueEvent): void => {
				handleEvent({ kind: 'binValue', binValue: event.binValue ?? null });
			},
		});

		this._customCard.mount(mountRoot);
	}

	/**
	 * PD-17168 — mount the card fields inside the storefront's https
	 * card-entry page, framed into `containerId`. For a page Adyen cannot
	 * accept as an origin (see needsCardFrame); the frame forwards every
	 * secured-field event and this service applies it, so the getters and
	 * tokenizeCardData behave exactly as with createSecureCardFields.
	 *
	 * The frame stays hidden behind the container's loading placeholder
	 * until it reports its fields mounted. If it never does (the page is
	 * missing, or Adyen refused it), onError is called once.
	 */
	public createFramedCardFields(
		containerId: string,
		options: {
			frameUrl: string;
			labels: AdyenCardFrameLabelsModel;
			placeholders: AdyenCardFramePlaceholdersModel;
			styles: AdyenCardFrameStylesModel;
			onCardBrandChange?: (brand: string | null) => void;
			onCardBinChange?: (bin: string | null) => void;
			onError?: (message: string) => void;
		},
	): void {
		const clientKey = this._config?.publicCredentials.clientKey;

		if (!this._isCardInitialized || this._config == null || clientKey == null) {
			throw new PaymentGatewayError(
				'Card service not initialized. Call initialize("CARD") first.',
				'NOT_INITIALIZED',
			);
		}

		const container = document.getElementById(containerId);

		if (container == null) {
			throw new PaymentGatewayError(
				'Adyen card frame container not found in the DOM',
				'NOT_INITIALIZED',
			);
		}

		const frameOrigin = new URL(options.frameUrl).origin;

		this._unmountCardFields();

		this._onCardBrandChange = options.onCardBrandChange ?? null;
		this._onCardBinChange = options.onCardBinChange ?? null;
		this._cardStateValid = false;
		this._cardPaymentMethodData = null;
		this._cardLastFour = null;
		this._cardBin = null;

		const iframe = document.createElement('iframe');
		iframe.src = options.frameUrl;
		iframe.title = options.labels.cardNumber;
		// Hidden until the fields are mounted: a frame that loaded something
		// else (e.g. the storefront's fallback page) must never show.
		iframe.style.cssText =
			'display:block;width:100%;height:0;border:0;visibility:hidden;background:transparent;';
		iframe.setAttribute('scrolling', 'no');

		let settled = false;
		const fail = (message: string): void => {
			if (settled) {
				return;
			}

			settled = true;
			options.onError?.(message);
		};

		const initMessage: AdyenCardFrameInitMessageModel = {
			protocol: ADYEN_CARD_FRAME_PROTOCOL,
			type: 'init',
			clientKey,
			environment: this._config.environment,
			styles: options.styles,
			labels: options.labels,
			placeholders: options.placeholders,
		};

		const handleLoad = (): void => {
			iframe.contentWindow?.postMessage(initMessage, frameOrigin);
		};

		const handleMessage = (event: MessageEvent): void => {
			if (event.source !== iframe.contentWindow || event.origin !== frameOrigin) {
				return;
			}

			const message = parseAdyenCardFrameToParentMessage(event.data);

			if (message == null) {
				return;
			}

			switch (message.type) {
				case 'mounted':
					settled = true;
					clearTimeout(mountTimeout);
					container
						.querySelectorAll(':scope > :not(iframe)')
						.forEach((child): void => {
							child.remove();
						});
					iframe.style.visibility = 'visible';
					break;
				case 'height':
					iframe.style.height = `${Math.ceil(message.height)}px`;
					break;
				case 'event':
					this._applySecuredFieldEvent(message.event);
					break;
				case 'error':
					fail(message.message);
					break;
			}
		};

		const mountTimeout = setTimeout((): void => {
			fail('Timed out waiting for the Adyen card frame');
		}, CARD_FRAME_MOUNT_TIMEOUT_MS);

		iframe.addEventListener('load', handleLoad);
		window.addEventListener('message', handleMessage);

		this._cardFrame = {
			iframe,
			removeListeners: (): void => {
				clearTimeout(mountTimeout);
				iframe.removeEventListener('load', handleLoad);
				window.removeEventListener('message', handleMessage);
			},
		};

		container.appendChild(iframe);
	}

	/**
	 * Apply one secured-field event to the card state — from the locally
	 * mounted Custom Card, or forwarded by the card-entry frame.
	 */
	private _applySecuredFieldEvent(event: AdyenSecuredFieldEventModel): void {
		switch (event.kind) {
			case 'change':
				this._cardStateValid = event.isValid;
				this._cardPaymentMethodData =
					event.isValid && event.paymentMethod != null
						? event.paymentMethod
						: null;
				break;
			case 'brand':
				this._detectedCardBrand = this._mapCardBrand(event.brand ?? undefined);
				this._onCardBrandChange?.(this._detectedCardBrand);
				break;
			case 'fieldValid':
				// The card number field reports its last digits when valid —
				// the only display data available from the secured fields.
				if (
					event.fieldType === 'encryptedCardNumber' &&
					event.endDigits != null &&
					event.endDigits !== ''
				) {
					this._cardLastFour = event.endDigits.slice(-4);
				}
				break;
			case 'binValue': {
				// PD-15014 — the secured number field reports the leading digits
				// (the BIN) as the shopper types. Capturing it here makes card
				// funding classification (/cardDetails) possible even when the
				// merchant account's tokenization response carries no card
				// additional data — which varies per account and per scheme, so
				// the server-side capture alone cannot be relied on. Fires
				// progressively; only a complete 6+ digit BIN is kept, and an
				// edit that shortens the number clears it again.
				const digits = (event.binValue ?? '').replace(/\D/g, '');
				const bin = /^\d{6,8}$/.test(digits) ? digits : null;

				if (bin !== this._cardBin) {
					this._cardBin = bin;
					this._onCardBinChange?.(bin);
				}
				break;
			}
		}
	}

	/** Remove whichever card fields are mounted, local or framed. */
	private _unmountCardFields(): void {
		if (this._customCard != null) {
			try {
				this._customCard.unmount();
			} catch {
				// The containers may already be gone (component unmounted)
			}
			this._customCard = null;
		}

		if (this._cardFrame != null) {
			this._cardFrame.removeListeners();
			this._cardFrame.iframe.remove();
			this._cardFrame = null;
		}
	}

	/**
	 * Collect the encrypted card data from the secured fields.
	 * Unlike Collect.JS there is no async round trip — the SDK encrypts as
	 * the shopper types and the blobs are already in the component state.
	 */
	public tokenizeCardData(
		formData: PaymentMethodFormData,
	): Promise<OpaqueData> {
		if (
			!this._isCardInitialized ||
			(this._customCard == null && this._cardFrame == null)
		) {
			throw new PaymentGatewayError(
				'Card service not initialized. Call initialize("CARD") first.',
				'NOT_INITIALIZED',
			);
		}

		if (!this._cardStateValid || this._cardPaymentMethodData == null) {
			throw new PaymentGatewayError(
				'Please complete the card number, expiration date, and security code fields.',
				'VALIDATION_ERROR',
			);
		}

		const paymentMethod: Record<string, unknown> = {
			type: 'scheme',
			...this._cardPaymentMethodData,
		};

		if (formData.nameOnCard !== '') {
			paymentMethod.holderName = formData.nameOnCard;
		}

		// Ridden inside the opaque payload; the backend strips it out of the
		// paymentMethod object and sends it as the top-level /payments
		// browserInfo, which Adyen's Uplift scoring consumes (PD-15229).
		const browserInfo = collectBrowserInfo();

		if (browserInfo != null) {
			paymentMethod.browserInfo = browserInfo;
		}

		return Promise.resolve({
			dataDescriptor: 'ADYEN_CARD',
			dataValue: JSON.stringify(paymentMethod),
		});
	}

	/**
	 * Serialize ACH bank account data from the plain form fields. Bank
	 * details are not PCI data; Adyen's API-only ACH integration accepts
	 * them directly.
	 */
	public tokenizeBankData(
		formData: PaymentMethodFormData,
	): Promise<OpaqueData> {
		if (!this._isCheckInitialized) {
			throw new PaymentGatewayError(
				'Check service not initialized. Call initialize("CHECK") first.',
				'NOT_INITIALIZED',
			);
		}

		const routingNumber = normalizeRoutingNumber(formData.routingNumber);
		const accountNumber = formData.accountNumber.replace(/\D/g, '');

		if (!/^\d{9}$/.test(routingNumber)) {
			throw new PaymentGatewayError(
				'Routing number must be 9 digits.',
				'VALIDATION_ERROR',
			);
		}

		// PD-16777 — Adyen stores a bank account with a zero-value check that
		// does not prove the routing number exists, so a typo surfaces only on
		// the first real debit. The prefix and checksum catch it here.
		if (!isAcceptedRoutingNumber(routingNumber)) {
			throw new PaymentGatewayError(
				ROUTING_NUMBER_INVALID_MESSAGE,
				'VALIDATION_ERROR',
			);
		}

		if (accountNumber.length < 4 || accountNumber.length > 17) {
			throw new PaymentGatewayError(
				'Please enter a valid bank account number.',
				'VALIDATION_ERROR',
			);
		}

		if (formData.nameOnAccount === '') {
			throw new PaymentGatewayError(
				'Name on account is required.',
				'VALIDATION_ERROR',
			);
		}

		const achPaymentMethod: Record<string, unknown> = {
			type: 'ach',
			bankAccountNumber: accountNumber,
			bankLocationId: routingNumber,
			ownerName: formData.nameOnAccount,
		};

		// Same sidecar as tokenizeCardData — stripped and hoisted server-side.
		const browserInfo = collectBrowserInfo();

		if (browserInfo != null) {
			achPaymentMethod.browserInfo = browserInfo;
		}

		return Promise.resolve({
			dataDescriptor: 'ADYEN_ACH',
			dataValue: JSON.stringify(achPaymentMethod),
		});
	}

	/**
	 * Whether the secured card fields currently hold complete, valid data.
	 */
	public isCardStateValid(): boolean {
		return this._cardStateValid;
	}

	/**
	 * Get the detected card brand from the secured card number field.
	 */
	public getDetectedCardBrand(): string | null {
		return this._detectedCardBrand;
	}

	/**
	 * Get the last four digits reported by the secured card number field.
	 */
	public getCardLastFour(): string | null {
		return this._cardLastFour;
	}

	/**
	 * Get the BIN (leading 6–8 digits) reported by the secured card number
	 * field, or null if a complete BIN was never observed.
	 */
	public getCardBin(): string | null {
		return this._cardBin;
	}

	/**
	 * Validate the non-secured form fields before tokenization (the card
	 * number/expiration/CVV live in Adyen's iframes and validate there).
	 */
	public validateFormData(formData: PaymentMethodFormData): boolean {
		const errors: Array<string> = [];

		if (formData.paymentType === PaymentMethodTypeEnum.CREDIT_CARD) {
			if (formData.nameOnCard === '') {
				errors.push('Name on card is required');
			}
		} else if (formData.paymentType === PaymentMethodTypeEnum.ACH) {
			if (formData.nameOnAccount === '') {
				errors.push('Name on account is required');
			}

			const routingNumber = normalizeRoutingNumber(formData.routingNumber);

			if (!/^\d{9}$/.test(routingNumber)) {
				errors.push('Routing number must be 9 digits');
			} else if (!isAcceptedRoutingNumber(routingNumber)) {
				// PD-16777 — see tokenizeBankData.
				errors.push(ROUTING_NUMBER_INVALID_MESSAGE);
			}

			if (formData.accountNumber.replace(/\D/g, '') === '') {
				errors.push('Account number is required');
			}
		}

		if (errors.length > 0) {
			throw new PaymentGatewayError(errors.join('. '), 'VALIDATION_ERROR');
		}

		return true;
	}

	/**
	 * Unmount the secured fields and reset per-form state. Safe to call
	 * multiple times.
	 */
	public cleanup(): void {
		this._unmountCardFields();

		this._cardStateValid = false;
		this._cardPaymentMethodData = null;
		this._detectedCardBrand = null;
		this._cardLastFour = null;
		this._cardBin = null;
		this._onCardBrandChange = null;
		this._onCardBinChange = null;
	}

	// -------------------------------------------------------------------------
	// Private Helpers
	// -------------------------------------------------------------------------

	private _prepareContainer(container: HTMLElement, cseType: string): void {
		container.innerHTML = '';
		container.setAttribute('data-cse', cseType);
	}

	/**
	 * Find the closest ancestor that contains every element — the Custom
	 * Card component mounts there and scans for the data-cse containers.
	 */
	private _findCommonAncestor(elements: Array<HTMLElement>): HTMLElement {
		let candidate: HTMLElement | null = elements[0] ?? null;

		while (candidate != null) {
			const containsAll = elements.every((element): boolean =>
				candidate != null ? candidate.contains(element) : false,
			);

			if (containsAll) {
				return candidate;
			}

			candidate = candidate.parentElement;
		}

		return document.body;
	}

	private async _loadSdk(environment?: string): Promise<void> {
		if (window.AdyenWeb != null) {
			return;
		}

		const baseUrl = AdyenFrontendService._getSdkBaseUrl(environment);
		AdyenFrontendService._injectAssets(baseUrl);

		const scriptUrl = `${baseUrl}/adyen.js`;
		const script = document.querySelector<HTMLScriptElement>(
			`script[src="${scriptUrl}"]`,
		);

		if (script == null) {
			throw new PaymentGatewayError(
				'Failed to inject the Adyen Web SDK script',
				'INIT_ERROR',
			);
		}

		await new Promise<void>((resolve, reject) => {
			// The script may already be loaded (preload path)
			if (window.AdyenWeb != null) {
				resolve();
				return;
			}

			const timeout = setTimeout((): void => {
				reject(
					new PaymentGatewayError(
						'Timed out loading the Adyen Web SDK',
						'TIMEOUT',
					),
				);
			}, 30000);

			script.addEventListener('load', (): void => {
				clearTimeout(timeout);
				resolve();
			});

			script.addEventListener('error', (): void => {
				clearTimeout(timeout);
				reject(
					new PaymentGatewayError(
						'Failed to load the Adyen Web SDK',
						'INIT_ERROR',
					),
				);
			});
		});
	}

	/**
	 * Map Adyen brand identifiers to our PaymentMethodSubtypeEnum labels.
	 */
	private _mapCardBrand(brand: string | undefined): string | null {
		switch (brand) {
			case 'visa':
				return 'Visa';
			case 'mc':
				return 'MasterCard';
			case 'amex':
				return 'Amex';
			case 'discover':
				return 'Discover';
			case 'diners':
				return 'Diners Club';
			case 'jcb':
				return 'JCB';
			default:
				return null;
		}
	}

	/**
	 * Get configuration from backend
	 */
	private async _getConfiguration(): Promise<PaymentGatewayConfig> {
		try {
			const response = await ApiService.apiGet<{
				success: boolean;
				data?: PaymentGatewayConfig;
				error?: string;
			}>('/payments/config/public');

			if (!response.success || response.data == null) {
				throw new Error(response.error ?? 'No configuration received');
			}

			return response.data;
		} catch (error) {
			throw new PaymentGatewayError(
				'Failed to fetch payment gateway configuration',
				'CONFIG_ERROR',
				error,
			);
		}
	}
}
